"use client";

import { m as motion, useReducedMotion, useInView } from "framer-motion";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Hourglass, Minus, Plus, Rocket, Zap } from "lucide-react";
import { localizedRoutes } from "@/lib/routes/routes";

interface WeeksNotMonthsVisualProps {
  isItalian: boolean;
}

/**
 * Sezione signature MVP: "Settimane, non mesi".
 * Due binari che girano in loop nello stesso tempo:
 *  - MVP con il metodo MCE: avanza fase per fase (Mappa, Costruisci con una
 *    tacca per settimana, Lancio, Evolvi), poi ricomincia.
 *  - Agenzia o software house: avanza piano e a scatti; quando si ferma
 *    compare "in attesa di approvazione". A fine ciclo è ancora alle specifiche.
 * Sotto: pro e contro onesti dei due modelli e i numeri dell'MVP.
 * Solo transform/opacity. Loop solo con la sezione a schermo;
 * useReducedMotion -> stato finale fermo, niente animazione.
 */

const EASE = [0.16, 1, 0.3, 1] as const;

// Un ciclo. mvp/trad = avanzamento (0-1) a fine passo, dur = secondi del passo.
// Dove trad non cambia rispetto al passo prima, la barra tradizionale è ferma.
const STEPS = [
  { mvp: 0.12, trad: 0.04, dur: 0.9 }, // Mappa
  { mvp: 0.24, trad: 0.04, dur: 0.55 }, // settimana 1
  { mvp: 0.36, trad: 0.08, dur: 0.55 }, // settimana 2
  { mvp: 0.48, trad: 0.08, dur: 0.55 }, // settimana 3
  { mvp: 0.6, trad: 0.12, dur: 0.55 }, // settimana 4
  { mvp: 0.72, trad: 0.12, dur: 0.55 }, // settimana 5
  { mvp: 0.86, trad: 0.16, dur: 0.7 }, // Lancio
  { mvp: 1, trad: 0.16, dur: 0.9 }, // Evolvi
  { mvp: 1, trad: 0.22, dur: 1.8 }, // pausa: MVP online, tradizionale alle specifiche
] as const;
const LAST = STEPS.length - 1;
const RESET_MS = 500;

// Confini delle fasi MVP sulla barra (Mappa | Costruisci | Lancio | Evolvi).
const MVP_BOUNDS = [0.12, 0.72, 0.86];
const MVP_COLS = "md:grid-cols-[12fr_60fr_14fr_14fr]";
// Fine di ogni settimana di sviluppo (passi 1-5).
const WEEK_ENDS = [0.24, 0.36, 0.48, 0.6, 0.72];

function mvpPhaseOf(step: number) {
  if (step < 0) return -1;
  if (step === 0) return 0;
  if (step <= 5) return 1;
  if (step === 6) return 2;
  return 3;
}

export function WeeksNotMonthsVisual({ isItalian }: WeeksNotMonthsVisualProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const show = inView || reduce;
  // Gate continuo (non "once"): il loop si ferma quando la sezione esce dallo
  // schermo e riparte da dove era quando rientra.
  const inViewLoop = useInView(ref, { margin: "-80px" });
  const running = inViewLoop && !reduce;

  const [step, setStep] = useState(-1);
  useEffect(() => {
    if (!running) return;
    const ms = step < 0 ? RESET_MS : STEPS[step].dur * 1000;
    const id = window.setTimeout(() => setStep((s) => (s >= LAST ? -1 : s + 1)), ms);
    return () => window.clearTimeout(id);
  }, [running, step]);

  const shown = reduce ? LAST : step;
  const current = shown >= 0 ? STEPS[shown] : null;
  const mvpProgress = current?.mvp ?? 0;
  const tradProgress = current?.trad ?? 0;
  const moveDur = reduce ? 0 : current ? current.dur : RESET_MS / 1000;
  const mvpPhase = mvpPhaseOf(shown);
  const tradPhase = current ? Math.min(4, Math.floor(tradProgress / 0.2)) : -1;
  const tradPaused =
    !reduce && shown > 0 && STEPS[shown].trad === STEPS[shown - 1].trad;

  const methodHref = localizedRoutes[isItalian ? "it" : "en"].method;

  const t = isItalian
    ? {
        tradLabel: "Agenzia o software house",
        tradRange: "3-6 mesi",
        tradPhases: ["Gara e preventivo", "Specifiche", "Riunioni", "Sviluppo del team", "Collaudo"],
        tradNow: "Ora:",
        tradNote: "Più persone al lavoro, ma ogni passaggio aspetta un'approvazione.",
        waiting: "In attesa di approvazione",
        mvpLabel: "MVP con me, metodo MCE",
        mvpRange: "4-6 settimane",
        mvpPhases: [
          { mark: "M", name: "Mappa", sub: "Perimetro firmato" },
          { mark: "C", name: "Costruisci", sub: "Settimane 1-5, aggiornamento scritto ogni settimana" },
          { mark: null, name: "Lancio", sub: "Settimana 6, online con clienti veri" },
          { mark: "E", name: "Evolvi", sub: "30 giorni di supporto" },
        ],
        methodLink: "Come funziona il metodo MCE",
        compare: [
          {
            title: "Agenzia o software house",
            pros: ["Più sviluppatori lavorano in parallelo", "Regge progetti molto grandi"],
            cons: [
              "Parli con 3-4 persone diverse e ogni scelta passa da una riunione",
              "Budget da 15.000 € in su",
              "3-6 mesi prima di andare online",
            ],
          },
          {
            title: "Con me",
            pros: [
              "Parli sempre con chi scrive il codice",
              "Uso l'AI per le parti ripetitive: le scelte e i controlli restano miei",
              // ponytail: prezzo scritto a mano come nel resto di /mvp; fonte: services.ts (mvp-saas, starter)
              "Prezzo fisso da 4.800 €, il codice è tuo",
            ],
            cons: ["Sono uno solo: una funzione principale alla volta, non 20 insieme"],
          },
        ],
        stats: [
          { value: "4-6", label: "settimane al lancio" },
          { value: "1", label: "referente unico" },
          { value: "100%", label: "codice tuo" },
          { value: "30gg", label: "di supporto incluso" },
        ],
      }
    : {
        tradLabel: "Agency or software house",
        tradRange: "3-6 months",
        tradPhases: ["Tender and quote", "Specs", "Meetings", "Team development", "Testing"],
        tradNow: "Now:",
        tradNote: "More people on it, but every step waits for a sign-off.",
        waiting: "Waiting for approval",
        mvpLabel: "MVP with me, MCE method",
        mvpRange: "4-6 weeks",
        mvpPhases: [
          { mark: "M", name: "Map", sub: "Signed scope" },
          { mark: "C", name: "Build", sub: "Weeks 1-5, a written update every week" },
          { mark: null, name: "Launch", sub: "Week 6, live with real customers" },
          { mark: "E", name: "Evolve", sub: "30 days of support" },
        ],
        methodLink: "How the MCE method works",
        compare: [
          {
            title: "Agency or software house",
            pros: ["More developers working in parallel", "Handles very large projects"],
            cons: [
              "You deal with 3-4 different people and every choice goes through a meeting",
              "Budget from €15,000 up",
              "3-6 months before going live",
            ],
          },
          {
            title: "With me",
            pros: [
              "You always talk to the person writing the code",
              "I use AI for the repetitive parts: the choices and the checks stay mine",
              "Fixed price from €4,800, the code is yours",
            ],
            cons: ["I'm one person: one core feature at a time, not 20 at once"],
          },
        ],
        stats: [
          { value: "4-6", label: "weeks to launch" },
          { value: "1", label: "single point of contact" },
          { value: "100%", label: "your own code" },
          { value: "30d", label: "of support included" },
        ],
      };

  return (
    <section className="section-padding bg-surface/50">
      <div className="container-wide" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={show ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-left mb-12"
        >
          <span className="font-mono text-label text-primary mb-3 block">Time to market</span>
          <h2 className="font-display text-h2 font-bold mb-4">
            {isItalian ? (
              <>
                Settimane, <span className="text-primary">non mesi</span>
              </>
            ) : (
              <>
                Weeks, <span className="text-primary">not months</span>
              </>
            )}
          </h2>
          <p className="text-body-lg text-muted-foreground xl:whitespace-nowrap">
            {isItalian
              ? "Lo stesso budget speso in settimane invece che in mesi e validazione reale prima che la concorrenza arrivi."
              : "The same budget spent in weeks instead of months, and real validation before the competition shows up."}
          </p>
        </motion.div>

        <div className="surface-card p-6 md:p-10">
          {/* Binario tradizionale: lento, a scatti */}
          <div className="mb-10">
            <div className="flex items-center justify-between gap-3 mb-3">
              <span className="inline-flex items-center gap-2 text-body font-medium text-muted-foreground">
                <Hourglass className="w-4 h-4 shrink-0" aria-hidden="true" />
                {t.tradLabel}
              </span>
              <span className="font-mono text-sm text-muted-foreground shrink-0">{t.tradRange}</span>
            </div>
            <div
              className="relative h-3 md:h-4 w-full rounded-full bg-background/60 overflow-hidden"
              aria-hidden="true"
            >
              <motion.div
                initial={false}
                animate={{ scaleX: tradProgress }}
                transition={{ duration: moveDur, ease: "linear" }}
                style={{ transformOrigin: "left" }}
                className="h-full w-full rounded-full bg-muted-foreground/30"
              />
              {[0.2, 0.4, 0.6, 0.8].map((x) => (
                <span
                  key={x}
                  className="absolute inset-y-0 w-px bg-foreground/10"
                  style={{ left: `${x * 100}%` }}
                />
              ))}
            </div>
            <ol className="hidden md:grid grid-cols-5 mt-2">
              {t.tradPhases.map((name, i) => (
                <li
                  key={name}
                  className={`text-xs transition-colors duration-500 ${
                    i === tradPhase ? "text-foreground" : "text-muted-foreground/60"
                  }`}
                >
                  {name}
                </li>
              ))}
            </ol>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
              <p className="text-sm text-muted-foreground/80">
                <span className="md:hidden">
                  {t.tradNow} {t.tradPhases[Math.max(0, tradPhase)]}.{" "}
                </span>
                {t.tradNote}
              </p>
              <motion.span
                initial={false}
                animate={{ opacity: tradPaused ? 1 : 0 }}
                transition={{ duration: 0.25 }}
                className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] px-2.5 py-0.5 font-mono text-[0.68rem] uppercase tracking-[0.06em] text-muted-foreground"
                aria-hidden="true"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400/80 animate-pulse" />
                {t.waiting}
              </motion.span>
            </div>
          </div>

          {/* Binario MVP: fase per fase con il metodo MCE, in loop */}
          <div>
            <div className="flex items-center justify-between gap-3 mb-3">
              <span className="inline-flex items-center gap-2 text-body font-semibold text-foreground">
                <Zap className="w-4 h-4 shrink-0 text-primary" aria-hidden="true" />
                {t.mvpLabel}
              </span>
              <span className="font-mono text-sm text-primary shrink-0">{t.mvpRange}</span>
            </div>
            <div
              className="relative h-6 md:h-8 w-full rounded-full bg-background/60 overflow-hidden"
              aria-hidden="true"
            >
              <motion.div
                initial={false}
                animate={{ scaleX: mvpProgress }}
                transition={{ duration: moveDur, ease: shown < 0 ? EASE : "easeInOut" }}
                style={{ transformOrigin: "left" }}
                className="h-full w-full rounded-full bg-gradient-to-r from-primary/70 to-primary shadow-[0_0_20px_hsl(var(--primary)/0.4)]"
              />
              {MVP_BOUNDS.map((x) => (
                <span
                  key={x}
                  className="absolute inset-y-0 w-px bg-background/70"
                  style={{ left: `${x * 100}%` }}
                />
              ))}
              {WEEK_ENDS.map((x, i) => {
                const lit = shown >= i + 1;
                return (
                  <motion.span
                    key={x}
                    initial={false}
                    animate={{ scale: lit ? 1 : 0.6, opacity: lit ? 1 : 0.35 }}
                    transition={{ duration: reduce ? 0 : 0.3 }}
                    className={`absolute top-1/2 -mt-[3px] -ml-[3px] h-1.5 w-1.5 rounded-full ${
                      lit ? "bg-white shadow-[0_0_6px_2px_rgba(255,255,255,0.7)]" : "bg-foreground/40"
                    }`}
                    style={{ left: `${x * 100}%` }}
                  />
                );
              })}
            </div>

            <ol className={`grid grid-cols-2 ${MVP_COLS} gap-x-3 gap-y-4 mt-4`}>
              {t.mvpPhases.map((p, i) => {
                const active = i === mvpPhase;
                const done = reduce || i < mvpPhase;
                return (
                  <li key={p.name} className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-mono text-xs font-bold transition-colors duration-300 ${
                          active
                            ? "bg-primary text-primary-foreground shadow-[0_0_14px_hsl(var(--primary)/0.55)]"
                            : done
                              ? "bg-primary/20 text-primary"
                              : "bg-background/60 text-muted-foreground"
                        }`}
                        aria-hidden="true"
                      >
                        {p.mark ?? <Rocket className="h-3.5 w-3.5" />}
                      </span>
                      <span
                        className={`text-sm font-semibold transition-colors duration-300 ${
                          active || done ? "text-foreground" : "text-muted-foreground"
                        }`}
                      >
                        {p.name}
                      </span>
                    </div>
                    <p className="mt-1 text-xs leading-snug text-muted-foreground">{p.sub}</p>
                  </li>
                );
              })}
            </ol>

            <Link
              href={methodHref}
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline underline-offset-4"
            >
              {t.methodLink}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          {/* Pro e contro dei due modelli */}
          <div className="grid gap-4 md:grid-cols-2 mt-10">
            {t.compare.map((col, ci) => (
              <motion.div
                key={col.title}
                initial={false}
                animate={show ? { opacity: 1, y: 0 } : { opacity: 0, y: reduce ? 0 : 12 }}
                transition={{ duration: reduce ? 0 : 0.45, delay: reduce ? 0 : 0.3 + ci * 0.1, ease: EASE }}
                className={`rounded-xl border p-5 md:p-6 ${
                  ci === 1
                    ? "border-primary/30 bg-primary/5"
                    : "border-[hsl(var(--border))] bg-background/40"
                }`}
              >
                <p
                  className={`font-mono text-label mb-4 ${
                    ci === 1 ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  {col.title}
                </p>
                <ul className="space-y-3">
                  {col.pros.map((text) => (
                    <li key={text} className="flex gap-3 text-sm text-foreground/90">
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                        <Plus className="h-3 w-3" aria-hidden="true" />
                      </span>
                      <span>
                        <span className="sr-only">Pro: </span>
                        {text}
                      </span>
                    </li>
                  ))}
                  {col.cons.map((text) => (
                    <li key={text} className="flex gap-3 text-sm text-muted-foreground">
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted-foreground/15">
                        <Minus className="h-3 w-3" aria-hidden="true" />
                      </span>
                      <span>
                        <span className="sr-only">{isItalian ? "Contro: " : "Con: "}</span>
                        {text}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Mini-stat onesti: dati concreti dell'MVP */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px mt-10 rounded-xl overflow-hidden bg-[hsl(var(--border))]">
            {t.stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={false}
                animate={show ? { opacity: 1, y: 0 } : { opacity: 0, y: reduce ? 0 : 12 }}
                transition={{
                  duration: reduce ? 0 : 0.45,
                  delay: reduce ? 0 : 0.6 + i * 0.1,
                  ease: EASE,
                }}
                className="bg-surface/80 px-4 py-5 text-left"
              >
                <span className="block font-display text-2xl md:text-3xl font-bold text-primary leading-none mb-1.5">
                  {stat.value}
                </span>
                <span className="block text-xs text-muted-foreground leading-snug">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
